// src/lib/riskEngine.js
// All quantitative risk calculations (runs client-side, no API needed)

// ============================================================
// RETURNS CALCULATION
// ============================================================
export function calcDailyReturns(prices) {
  const returns = [];
  for (let i = 1; i < prices.length; i++) {
    if (prices[i - 1] > 0) returns.push((prices[i] - prices[i - 1]) / prices[i - 1]);
  }
  return returns;
}

export function calcPortfolioReturns(positionReturns, weights) {
  // positionReturns: {ticker: [r1,r2,...]}
  // weights: {ticker: w} (sum to 1, negative for shorts)
  const tickers = Object.keys(weights);
  const minLen = Math.min(...tickers.map(t => positionReturns[t]?.length || 0));
  if (minLen < 10) return [];

  const portfolioReturns = [];
  for (let i = 0; i < minLen; i++) {
    let r = 0;
    for (const ticker of tickers) {
      r += (weights[ticker] || 0) * (positionReturns[ticker][i] || 0);
    }
    portfolioReturns.push(r);
  }
  return portfolioReturns;
}

// ============================================================
// DESCRIPTIVE STATS
// ============================================================
export function mean(arr) { return arr.length ? arr.reduce((s, x) => s + x, 0) / arr.length : 0; }
export function stdDev(arr) {
  if (arr.length < 2) return 0;
  const m = mean(arr);
  return Math.sqrt(arr.reduce((s, x) => s + (x - m) ** 2, 0) / (arr.length - 1));
}

// ============================================================
// VALUE AT RISK (Historical Simulation)
// ============================================================
export function calcVaR(returns, confidence = 0.95, portfolioValue = 1) {
  if (!returns.length) return 0;
  const sorted = [...returns].sort((a, b) => a - b);
  const idx = Math.floor((1 - confidence) * sorted.length);
  return -sorted[idx] * portfolioValue;
}

export function calcCVaR(returns, confidence = 0.95, portfolioValue = 1) {
  if (!returns.length) return 0;
  const sorted = [...returns].sort((a, b) => a - b);
  const cutoff = Math.floor((1 - confidence) * sorted.length);
  const tail = sorted.slice(0, cutoff + 1);
  return -mean(tail) * portfolioValue;
}

// ============================================================
// VOLATILITY (annualized)
// ============================================================
export function calcAnnualizedVol(dailyReturns) {
  return stdDev(dailyReturns) * Math.sqrt(252);
}

// ============================================================
// SHARPE & SORTINO (using 5.25% risk-free rate approximation)
// ============================================================
export function calcSharpe(dailyReturns, rfRate = 0.0525) {
  if (dailyReturns.length < 20) return null;
  const annReturn = mean(dailyReturns) * 252;
  const annVol = calcAnnualizedVol(dailyReturns);
  return annVol > 0 ? (annReturn - rfRate) / annVol : null;
}

export function calcSortino(dailyReturns, rfRate = 0.0525) {
  if (dailyReturns.length < 20) return null;
  const annReturn = mean(dailyReturns) * 252;
  const downside = dailyReturns.filter(r => r < 0);
  const downsideVol = stdDev(downside) * Math.sqrt(252);
  return downsideVol > 0 ? (annReturn - rfRate) / downsideVol : null;
}

// ============================================================
// MAX DRAWDOWN
// ============================================================
export function calcMaxDrawdown(prices) {
  if (prices.length < 2) return 0;
  let peak = prices[0];
  let maxDD = 0;
  for (const p of prices) {
    if (p > peak) peak = p;
    const dd = (peak - p) / peak;
    if (dd > maxDD) maxDD = dd;
  }
  return maxDD;
}

// ============================================================
// BETA vs SPY + DOLLAR BETA
// ============================================================
export function calcBeta(portfolioReturns, spyReturns) {
  const minLen = Math.min(portfolioReturns.length, spyReturns.length);
  if (minLen < 20) return null;
  const pr = portfolioReturns.slice(-minLen);
  const sr = spyReturns.slice(-minLen);
  if (pr.length !== sr.length) return null;
  const cov = calcCovariance(pr, sr);
  const spyVar = calcVariance(sr);
  if (spyVar <= 0) return null;
  const beta = cov / spyVar;
  return Math.abs(beta) > 5 ? null : beta;
}

// Dollar Beta = beta * NAV (SPY-equivalent notional exposure)
export function calcDollarBeta(beta, portfolioNav) {
  if (beta == null || !portfolioNav) return null;
  return beta * portfolioNav;
}

// Per-position dollar beta contribution
export function calcPositionDollarBetas(positions, beta, portfolioNav) {
  if (beta == null || !portfolioNav) return [];
  return positions.map(p => {
    const mv = p.market_value || 0;
    return { ...p, weight: mv / portfolioNav, dollar_beta: (mv / portfolioNav) * beta * portfolioNav };
  });
}

function calcVariance(arr) {
  if (arr.length < 2) return 0;
  const m = mean(arr);
  return arr.reduce((s, x) => s + (x - m) ** 2, 0) / (arr.length - 1);
}

function calcCovariance(x, y) {
  if (x.length !== y.length || x.length < 2) return 0;
  const mx = mean(x), my = mean(y);
  return x.reduce((s, xi, i) => s + (xi - mx) * (y[i] - my), 0) / (x.length - 1);
}

// ============================================================
// CORRELATION MATRIX
// ============================================================
export function calcCorrelationMatrix(returnsMap) {
  const tickers = Object.keys(returnsMap);
  const matrix = {};
  for (const t1 of tickers) {
    matrix[t1] = {};
    for (const t2 of tickers) {
      const r1 = returnsMap[t1], r2 = returnsMap[t2];
      const minLen = Math.min(r1.length, r2.length);
      if (minLen < 10 || t1 === t2) { matrix[t1][t2] = t1 === t2 ? 1 : null; continue; }
      const s1 = stdDev(r1.slice(-minLen)), s2 = stdDev(r2.slice(-minLen));
      const cov = calcCovariance(r1.slice(-minLen), r2.slice(-minLen));
      matrix[t1][t2] = (s1 > 0 && s2 > 0) ? cov / (s1 * s2) : null;
    }
  }
  return matrix;
}

// ============================================================
// FAMA-FRENCH OLS REGRESSION
// Regress portfolio excess returns on: Mkt-RF, SMB, HML, MOM
// ============================================================
export function runFamaFrenchRegression(portfolioReturns, factorReturns) {
  // factorReturns: [{mkt_rf, smb, hml, rmw, cma, mom, rf}, ...] aligned with portfolioReturns
  // Uses real Ken French daily factors when available (fetched via edge function)
  const n = Math.min(portfolioReturns.length, factorReturns.length);
  if (n < 30) return null;

  // Excess portfolio returns (portfolio return - risk-free rate)
  const Y = portfolioReturns.slice(-n).map((r, i) => r - (factorReturns[i]?.rf || 0.0002));
  // Regressors: intercept + Mkt-RF + SMB + HML + RMW + CMA + MOM (FF6)
  const X = factorReturns.slice(-n).map(f => [1, f.mkt_rf, f.smb, f.hml, f.rmw ?? 0, f.cma ?? 0, f.mom]);

  try {
    const coeffs = olsEstimate(X, Y);
    const Yhat = X.map(row => row.reduce((s, x, j) => s + x * coeffs[j], 0));
    const SS_res = Y.reduce((s, y, i) => s + (y - Yhat[i]) ** 2, 0);
    const SS_tot = Y.reduce((s, y) => s + (y - mean(Y)) ** 2, 0);
    const r2 = SS_tot > 0 ? 1 - SS_res / SS_tot : 0;

    return {
      // alpha excluded from display — unreliable without long time series
      market_beta: coeffs[1],   // FF market beta (distinct from raw SPY beta)
      smb: coeffs[2],           // size factor loading
      hml: coeffs[3],           // value factor loading
      rmw: coeffs[4],           // profitability factor loading
      cma: coeffs[5],           // investment factor loading
      mom: coeffs[6],           // momentum factor loading
      r_squared: r2,            // fraction of variance explained (annual)
      observations: n,
      source: factorReturns[0]?._source || 'estimated'
    };
  } catch { return null; }
}

function olsEstimate(X, Y) {
  // Simple OLS for small matrices (4 factors + constant)
  const k = X[0].length;
  const XtX = Array.from({length: k}, (_, i) => Array.from({length: k}, (_, j) =>
    X.reduce((s, row) => s + row[i] * row[j], 0)));
  const XtY = Array.from({length: k}, (_, i) => X.reduce((s, row, r) => s + row[i] * Y[r], 0));
  return gaussianElimination(XtX, XtY);
}

function gaussianElimination(A, b) {
  const n = b.length;
  const M = A.map((row, i) => [...row, b[i]]);
  for (let i = 0; i < n; i++) {
    let maxRow = i;
    for (let k = i + 1; k < n; k++) if (Math.abs(M[k][i]) > Math.abs(M[maxRow][i])) maxRow = k;
    [M[i], M[maxRow]] = [M[maxRow], M[i]];
    for (let k = i + 1; k < n; k++) {
      const c = M[k][i] / M[i][i];
      for (let j = i; j <= n; j++) M[k][j] -= c * M[i][j];
    }
  }
  const x = new Array(n).fill(0);
  for (let i = n - 1; i >= 0; i--) {
    x[i] = M[i][n] / M[i][i];
    for (let k = i - 1; k >= 0; k--) M[k][n] -= M[k][i] * x[i];
  }
  return x;
}

// ============================================================
// APPROXIMATE FF FACTOR RETURNS (when live data unavailable)
// Based on historical averages — labeled as "estimated" in UI
// ============================================================
export function getApproximateFFFactors(n = 252) {
  // Fallback only — used when edge function FF fetch fails
  // Based on long-run historical daily averages (annualized / 252)
  const factors = [];
  for (let i = 0; i < n; i++) {
    factors.push({
      _source: 'estimated',
      mkt_rf: 0.000268 + (Math.random() - 0.5) * 0.008,
      smb:    0.000079 + (Math.random() - 0.5) * 0.004,
      hml:    0.000087 + (Math.random() - 0.5) * 0.004,
      rmw:    0.000100 + (Math.random() - 0.5) * 0.003,
      cma:    0.000075 + (Math.random() - 0.5) * 0.003,
      mom:    0.000119 + (Math.random() - 0.5) * 0.005,
      rf:     0.0002,
    });
  }
  return factors;
}

// ============================================================
// EXPOSURE CALCULATIONS
// ============================================================
export function calcExposures(positions, totalNav) {
  let longExposure = 0, shortExposure = 0;
  const sectorWeights = {}, assetClassWeights = {};

  for (const p of positions) {
    const mv = p.market_value || (p.current_price * p.quantity);
    const absMv = Math.abs(mv);

    if (p.direction === 'long') longExposure += absMv;
    else shortExposure += absMv;

    const sectorKey = p.sector || p.securities?.sector || 'Unknown';
    sectorWeights[sectorKey] = (sectorWeights[sectorKey] || 0) + mv;

    const acKey = p.asset_class || p.securities?.asset_class || 'Unknown';
    assetClassWeights[acKey] = (assetClassWeights[acKey] || 0) + mv;
  }

  const grossExposure = longExposure + shortExposure;
  const netExposure = longExposure - shortExposure;
  const nav = totalNav || grossExposure;

  // Normalize sector weights to % of gross exposure
  const normalizedSector = {};
  for (const [k, v] of Object.entries(sectorWeights)) {
    normalizedSector[k] = nav > 0 ? (v / nav) * 100 : 0;
  }
  const normalizedAC = {};
  for (const [k, v] of Object.entries(assetClassWeights)) {
    normalizedAC[k] = nav > 0 ? (v / nav) * 100 : 0;
  }

  return {
    longExposure, shortExposure, grossExposure, netExposure,
    netExposurePct: nav > 0 ? (netExposure / nav) * 100 : 0,
    grossExposurePct: nav > 0 ? (grossExposure / nav) * 100 : 0,
    sectorWeights: normalizedSector,
    assetClassWeights: normalizedAC
  };
}

// ============================================================
// CONCENTRATION (Herfindahl-Hirschman Index)
// ============================================================
export function calcHHI(positions, totalValue) {
  if (!positions.length || !totalValue) return 0;
  return positions.reduce((s, p) => {
    const w = Math.abs(p.market_value || 0) / totalValue;
    return s + w * w;
  }, 0);
}

// ============================================================
// CONCENTRATION ALERTS
// ============================================================
export function checkConcentrationAlerts(positions, totalNav, rules = []) {
  const alerts = [];
  const defaultRules = [
    { type: 'max_position_pct', threshold: 5 },
    { type: 'max_sector_pct', threshold: 35 }
  ];
  const activeRules = rules.length ? rules : defaultRules;

  for (const rule of activeRules) {
    if (rule.type === 'max_position_pct') {
      for (const p of positions) {
        const pct = Math.abs(p.market_value || 0) / totalNav * 100;
        if (pct > rule.threshold) {
          alerts.push({
            severity: pct > rule.threshold * 1.5 ? 'critical' : 'warning',
            message: `${p.ticker || p.cusip} is ${pct.toFixed(1)}% of NAV (limit: ${rule.threshold}%)`,
            type: 'concentration'
          });
        }
      }
    }
  }
  return alerts;
}
